import React from 'react';
import { Link, useLocation } from 'react-router-dom';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  return (
    <footer className="bg-primary-indigo dark:bg-neutral-dark-100 text-white border-t border-primary-indigo/20 dark:border-neutral-dark-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Brand Column */}
          <div className="col-span-2">
            <h3 className="text-2xl md:text-3xl font-logo font-bold mb-4">
              <span className="font-bold">SMPL</span>
              <span className="font-normal ml-1">Digital</span>
            </h3>
            <p className="text-neutral-200 dark:text-neutral-dark-700 mb-6 max-w-md leading-relaxed">
              Digital. Simplified.
            </p>
            <p className="text-neutral-200 dark:text-neutral-dark-700 mb-6 max-w-md leading-relaxed">
              We deliver digital solutions that grow your business—no jargon, no complexity, just results.
            </p>

            {/* Social Media Links */}
            <div className="flex space-x-4">

              <a
                href="https://www.linkedin.com/company/smpl-digital/"
                className="inline-flex items-center justify-center w-11 h-11 -m-2.5 text-neutral-200 dark:text-neutral-dark-700 hover:text-primary-rose rounded-lg hover:bg-white/10 transition-colors"
                aria-label="LinkedIn"
                title="Connect with us on LinkedIn"
              >
                <svg className="w-6 h-6" aria-hidden="true" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Services</h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/services/seo"
                  className="text-neutral-200 dark:text-neutral-dark-700 hover:text-primary-rose transition-colors"
                >
                  SEO
                </Link>
              </li>
              <li>
                <Link
                  to="/services/website-development"
                  className="text-neutral-200 dark:text-neutral-dark-700 hover:text-primary-rose transition-colors"
                >
                  Website Development
                </Link>
              </li>
              <li>
                <Link
                  to="/services/ai-automation"
                  className="text-neutral-200 dark:text-neutral-dark-700 hover:text-primary-rose transition-colors"
                >
                  AI & Automations
                </Link>
              </li>
              <li>
                <Link
                  to="/services/hubspot"
                  className="text-neutral-200 dark:text-neutral-dark-700 hover:text-primary-rose transition-colors"
                >
                  HubSpot Services
                </Link>
              </li>
              <li>
                <Link
                  to="/services/custom-apps"
                  className="text-neutral-200 dark:text-neutral-dark-700 hover:text-primary-rose transition-colors"
                >
                  Custom Apps
                </Link>
              </li>
              <li>
                <Link
                  to="/services/ppc"
                  className="text-neutral-200 dark:text-neutral-dark-700 hover:text-primary-rose transition-colors"
                >
                  PPC Advertising
                </Link>
              </li>
              <li>
                <Link
                  to="/services/content-marketing"
                  className="text-neutral-200 dark:text-neutral-dark-700 hover:text-primary-rose transition-colors"
                >
                  Content Marketing
                </Link>
              </li>
              <li>
                <Link
                  to="/services/social-media"
                  className="text-neutral-200 dark:text-neutral-dark-700 hover:text-primary-rose transition-colors"
                >
                  Social Media Marketing
                </Link>
              </li>
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Company</h4>
            <ul className="space-y-2">
              <li>
                {isHomePage ? (
                  <a
                    href="#about"
                    className="text-neutral-200 dark:text-neutral-dark-700 hover:text-primary-rose transition-colors"
                  >
                    About Us
                  </a>
                ) : (
                  <Link
                    to="/#about"
                    className="text-neutral-200 dark:text-neutral-dark-700 hover:text-primary-rose transition-colors"
                  >
                    About Us
                  </Link>
                )}
              </li>
              <li>
                <Link
                  to="/markets"
                  className="text-neutral-200 dark:text-neutral-dark-700 hover:text-primary-rose transition-colors"
                >
                  Markets
                </Link>
              </li>
              <li>
                <Link
                  to="/case-studies"
                  className="text-neutral-200 dark:text-neutral-dark-700 hover:text-primary-rose transition-colors"
                >
                  Case Studies
                </Link>
              </li>
              <li>
                {isHomePage ? (
                  <a
                    href="#contact"
                    className="text-neutral-200 dark:text-neutral-dark-700 hover:text-primary-rose transition-colors"
                  >
                    Contact Us
                  </a>
                ) : (
                  <Link
                    to="/contact"
                    className="text-neutral-200 dark:text-neutral-dark-700 hover:text-primary-rose transition-colors"
                  >
                    Contact Us
                  </Link>
                )}
              </li>
              <li>
                <Link
                  to="/privacy-policy"
                  className="text-neutral-200 dark:text-neutral-dark-700 hover:text-primary-rose transition-colors"
                >
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 dark:border-neutral-dark-300 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm mb-4">
            <p className="text-neutral-200 dark:text-neutral-dark-700">
              © {currentYear} SMPL Digital. All rights reserved.
            </p>
            <p className="text-neutral-200 dark:text-neutral-dark-700 mt-2 md:mt-0 font-medium">
              Digital. Simplified.
            </p>
          </div>

          {/* Company Registration Details */}
          <div className="text-center text-xs text-neutral-300 dark:text-neutral-dark-600 space-y-1">
            <p>
              Louise Royal Marketing Ltd. Trading as SMPL Digital | Registered in England and Wales
            </p>
            <p>
              Company Number: 15638951
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
